import React, {
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocalStorage } from '@makeably/creativex-design-system';
import GridControls, {
  FILL_COLORS,
  LINE_COLORS,
} from 'components/internal/review/GridControls';
import VideoPlayer from 'components/organisms/VideoPlayer';
import Gridlines from 'components/reusable/GridLines';
import styles from './Asset.module.css';

const propTypes = {
  checkId: PropTypes.number,
  isVideo: PropTypes.bool,
  onAssetReady: PropTypes.func,
  sourceUrl: PropTypes.string,
};

const defaultProps = {
  checkId: undefined,
  isVideo: false,
  onAssetReady: undefined,
  sourceUrl: undefined,
};

const GRID_X = 5;
const GRID_Y = 8;
const GRID_COUNT = GRID_X * GRID_Y;

function Asset({
  checkId,
  isVideo,
  onAssetReady,
  sourceUrl,
}) {
  const [gridFillColor, setGridFillColor] = useLocalStorage('cxIntGridFillColor', FILL_COLORS[0]);
  const [gridLineColor, setGridLineColor] = useLocalStorage('cxIntGridLineColor', LINE_COLORS[0]);
  const [showGrid, setShowGrid] = useState(false);
  const [gridHeight, setGridHeight] = useState(1);
  const [gridOffsetLeft, setGridOffsetLeft] = useState(0);
  const [gridOffsetTop, setGridOffsetTop] = useState(0);
  const [gridWidth, setGridWidth] = useState(1);
  const [selectedCoordinates, setSelectedCoordinates] = useState([]);

  useEffect(() => {
    setGridHeight(1);
    setGridOffsetLeft(0);
    setGridOffsetTop(0);
    setGridWidth(1);
  }, [sourceUrl]);

  useEffect(() => {
    setSelectedCoordinates([]);
  }, [checkId]);

  const handleSelectedCoordinates = (x, y) => {
    setSelectedCoordinates((last) => {
      const filtered = last.filter((p) => !(p.x === x && p.y === y));
      const isAdded = filtered.length === last.length;

      if (isAdded) {
        return [...filtered, {
          x,
          y,
        }];
      }
      return filtered;
    });
  };

  const setImageGrid = (event) => {
    const image = event.target;
    const {
      width,
      height,
    } = image;

    const imageRectangle = image.getBoundingClientRect();
    const imageParentRectangle = image.parentElement.getBoundingClientRect();

    // Given the image and image parent div, find the distance from the top left
    // corner of the image parent div to the top left corner of the image
    const newGridOffsetLeft = imageRectangle.x - imageParentRectangle.x;
    const newGridOffsetTop = imageRectangle.y - imageParentRectangle.y;

    setGridWidth(width);
    setGridHeight(height);
    setGridOffsetLeft(newGridOffsetLeft);
    setGridOffsetTop(newGridOffsetTop);
  };

  const handleImageLoad = (event) => {
    setImageGrid(event);
    onAssetReady?.();
  };

  const setVideoGrid = (htmlVideo) => {
    const videoContainerRect = htmlVideo.getBoundingClientRect();

    // By default, the video width and height will equal the container size
    let newGridWidth = videoContainerRect.width;
    let newGridHeight = videoContainerRect.height;
    let newGridOffsetTop = 0;
    let newGridOffsetLeft = 0;

    const videoAspectRatio = (htmlVideo.videoWidth / htmlVideo.videoHeight);

    // If container width:height ratio is less than actual width:height ratio, we
    // know that the video is bound by it's width
    const boundByWidth = (newGridWidth / newGridHeight) <= videoAspectRatio;

    if (boundByWidth) {
      // When bound by width, find the real video height
      newGridHeight = (htmlVideo.videoHeight / htmlVideo.videoWidth) * newGridWidth;
      // The video will be centered in the container, so the offset will be half
      // of the difference from the video container height and
      newGridOffsetTop = Math.ceil((videoContainerRect.height - newGridHeight) / 2);
    } else {
      newGridWidth = videoAspectRatio * newGridHeight;
      newGridOffsetLeft = Math.ceil((videoContainerRect.width - newGridWidth) / 2);
    }

    setGridWidth(Math.ceil(newGridWidth));
    setGridHeight(Math.ceil(newGridHeight));
    setGridOffsetLeft(newGridOffsetLeft);
    setGridOffsetTop(newGridOffsetTop);
  };

  const handleVideoReady = (htmlVideo) => {
    setVideoGrid(htmlVideo);
    onAssetReady?.();
  };

  return (
    <>
      <div className={styles.asset}>
        <Gridlines
          height={gridHeight}
          hideGrid={!showGrid}
          lineColor={gridLineColor}
          offsetLeft={gridOffsetLeft}
          offsetTop={gridOffsetTop}
          rectangleColor={gridFillColor}
          selectedCoordinates={selectedCoordinates}
          width={gridWidth}
          onSelection={handleSelectedCoordinates}
        >
          { isVideo && (
            <div className={styles.video}>
              <VideoPlayer
                source={sourceUrl}
                hasOptions
                onError={() => onAssetReady?.()}
                onReady={handleVideoReady}
              />
            </div>
          ) }
          { !isVideo && (
            <img
              alt="audit-post-asset"
              src={sourceUrl}
              width="100%"
              onError={() => onAssetReady?.()}
              onLoad={handleImageLoad}
            />
          ) }
        </Gridlines>
      </div>
      <GridControls
        gridFillColor={gridFillColor}
        gridLineColor={gridLineColor}
        selectedRatio={selectedCoordinates.length / GRID_COUNT}
        showGrid={showGrid}
        onGridFillColorChange={(value) => setGridFillColor(value)}
        onGridLineColorChange={(value) => setGridLineColor(value)}
        onShowGridChange={setShowGrid}
      />
    </>
  );
}

Asset.propTypes = propTypes;
Asset.defaultProps = defaultProps;

export default Asset;
